import { ReportRow, InvoiceStatus } from "./types";
import { InvoiceRecord } from "src/config/invoice-tracker-client";
import { populateFunctionalFields } from "./utils";

/**
 * Reconciles a report row against InvoiceTracker (Centauri) records.
 * Centauri is source of truth for amounts. Populates functional currency
 * fields (functionalInvoicesIssued, fxRate, funcCurrency) from tracker data,
 * then precomputes all functional currency columns.
 *
 * @param tacBillingPeriods When provided (for T@C deals), only tracker records
 *   whose billingPeriod (YYYY-MM) is in this set are counted. This scopes
 *   invoicesIssued to only the active tacBreakdown period.
 */
export function reconcileRow(
  row: ReportRow,
  trackerRecords: InvoiceRecord[],
  tacBillingPeriods?: Set<string>,
): ReportRow {
  const boundary = row.firstInvoiceBreakdownPeriod;
  let currencyMatched = trackerRecords.filter(
    (r) =>
      r.invoiceCurrencyCode === row.currencyCode &&
      (!boundary || r.billingPeriod >= boundary),
  );

  // T@C deals: further restrict to only billing periods that map to active tacBreakdown periods
  if (tacBillingPeriods) {
    currencyMatched = currencyMatched.filter((r) =>
      tacBillingPeriods.has(r.billingPeriod),
    );
  }

  const trackerTotal = currencyMatched.reduce((sum, r) => sum + r.amount, 0);
  const facdTotal = row.invoicesIssued;
  const status = determineStatus(
    facdTotal,
    trackerTotal,
    currencyMatched.length,
  );

  // Centauri (via DDB tracker) is the sole source of truth for invoices issued
  const invoicesIssued = trackerTotal;

  // Functional currency: sum functionalAmount from Centauri directly
  const functionalInvoicesIssued = currencyMatched.reduce(
    (sum, r) => sum + r.functionalAmount,
    0,
  );

  // FX rate: prefer FACD config rate when available, fall back to Centauri last invoice rate
  const lastRecord = currencyMatched
    .filter((r) => r.exchangeRate > 0)
    .sort((a, b) =>
      (b.glPostedDate ?? "").localeCompare(a.glPostedDate ?? ""),
    )[0];
  const fxRate =
    row.fxRate > 0 ? row.fxRate : (lastRecord?.exchangeRate ?? 1.0);

  // Functional currency code from Centauri ledger currency
  const funcCurrency = lastRecord?.functionalCurrencyCode ?? row.funcCurrency;

  const result: ReportRow = {
    ...row,
    invoiceStatus: status,
    trackerTotal,
    invoicesIssued,
    functionalInvoicesIssued,
    fxRate,
    funcCurrency,
    stEnding: row.stIssuance + row.cumulativeReclass - invoicesIssued,
    ltEnding: row.ltIssuance - row.cumulativeReclass,
    funcStIssuance: 0,
    funcStReclass: 0,
    funcStEnding: 0,
    stFxReval: 0,
    funcLtIssuance: 0,
    funcLtReclass: 0,
    funcLtEnding: 0,
    ltFxReval: 0,
  };
  populateFunctionalFields(result);
  return result;
}

/**
 * Determines invoice reconciliation status based on FACD vs Tracker totals.
 */
function determineStatus(
  facdTotal: number,
  trackerTotal: number,
  trackerRecordCount: number,
): InvoiceStatus {
  if (trackerRecordCount === 0) {
    return "unreconciled";
  }
  if (facdTotal === 0 && trackerTotal > 0) {
    return "tracker-sourced";
  }
  if (Math.abs(facdTotal - trackerTotal) <= 1) {
    return "confirmed";
  }
  return "amount-mismatch";
}
